/**
 * Minimal JWT payload decoder for client-side use.
 *
 * We don't verify the signature here — the SPA already trusts the tokens
 * Cognito just returned. The Lambda authoriser does proper JWT verification
 * with `aws-jwt-verify` against the JWKS. Use this purely for reading
 * claims (email, sub, expiry) so we can render the right username and
 * decide when to refresh.
 *
 * No `jwt-decode` dependency — the implementation is ~10 lines and the
 * dep doesn't earn its weight in the bundle.
 */

export interface IdTokenClaims {
  /** Cognito user `sub` — stable across email changes. */
  readonly sub: string;
  /** Verified email address. */
  readonly email: string;
  /** Issued-at, epoch seconds. */
  readonly iat: number;
  /** Expiry, epoch seconds. */
  readonly exp: number;
}

function base64UrlDecode(input: string): string {
  // base64url → base64: swap chars and pad to a multiple of 4.
  const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const padding = (4 - (base64.length % 4)) % 4;
  return atob(base64 + '='.repeat(padding));
}

export function decodeIdToken(jwt: string): IdTokenClaims {
  const parts = jwt.split('.');
  if (parts.length !== 3) {
    throw new Error('Invalid JWT — expected three dot-separated segments.');
  }
  // parts.length === 3 above, so the tuple cast is sound: it tells TS
  // (which can't follow through .length narrowing on a split) that each
  // index is a string, without adding an unreachable `?? ''` branch the
  // coverage gate would flag.
  const [, payloadBase64] = parts as [string, string, string];
  const payloadJson = base64UrlDecode(payloadBase64);
  const claims = JSON.parse(payloadJson) as Partial<IdTokenClaims>;

  if (typeof claims.sub !== 'string' || typeof claims.email !== 'string') {
    throw new Error('Invalid ID token — sub or email missing.');
  }
  if (typeof claims.iat !== 'number' || typeof claims.exp !== 'number') {
    throw new Error('Invalid ID token — iat or exp missing.');
  }

  return {
    sub: claims.sub,
    email: claims.email,
    iat: claims.iat,
    exp: claims.exp,
  };
}
